import { useCallback, useEffect, useRef } from "react";

function emptyHtml(html) {
    const t = (html || "").trim();
    return !t || t === "<br>" || t === "<p><br></p>" || t === "<p></p>";
}

function ToolbarButton({ title, active, onClick, children }) {
    return (
        <button
            type="button"
            title={title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
                active
                    ? "bg-[#4A0E17] text-white"
                    : "text-[#4A0E17] hover:bg-[#F2EFE9]"
            }`}
        >
            {children}
        </button>
    );
}

export default function RichTextEditor({ value, onChange, placeholder = "Write your article…", minHeight = 280 }) {
    const editorRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const syncingRef = useRef(false);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const emitChange = useCallback(() => {
        if (!editorRef.current || syncingRef.current) return;
        const html = editorRef.current.innerHTML;
        onChangeRef.current(emptyHtml(html) ? "" : html);
    }, []);

    useEffect(() => {
        if (!editorRef.current) return;
        const next = value || "";
        const current = editorRef.current.innerHTML;
        if (next === current || (emptyHtml(next) && emptyHtml(current))) return;
        syncingRef.current = true;
        editorRef.current.innerHTML = next;
        syncingRef.current = false;
    }, [value]);

    const run = (command, valueArg = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, valueArg);
        emitChange();
    };

    const setBlock = (tag) => {
        editorRef.current?.focus();
        document.execCommand("formatBlock", false, tag);
        emitChange();
    };

    const addLink = () => {
        const url = window.prompt("Link URL");
        if (!url) return;
        run("createLink", url);
    };

    return (
        <div className="blog-editor overflow-hidden rounded-xl border border-[#E8E4DE] bg-[#FFFEF9] shadow-sm">
            <div className="flex flex-wrap items-center gap-1 border-b border-[#E8E4DE] bg-gradient-to-b from-[#FFFCF6] to-[#FFFEF9] px-3 py-2">
                <ToolbarButton title="Heading 2" onClick={() => setBlock("h2")}>H2</ToolbarButton>
                <ToolbarButton title="Heading 3" onClick={() => setBlock("h3")}>H3</ToolbarButton>
                <span className="mx-1 h-4 w-px bg-[#E8E4DE]" aria-hidden />
                <ToolbarButton title="Bold" onClick={() => run("bold")}><strong>B</strong></ToolbarButton>
                <ToolbarButton title="Italic" onClick={() => run("italic")}><em>I</em></ToolbarButton>
                <ToolbarButton title="Underline" onClick={() => run("underline")}><span className="underline">U</span></ToolbarButton>
                <ToolbarButton title="Strikethrough" onClick={() => run("strikeThrough")}><span className="line-through">S</span></ToolbarButton>
                <span className="mx-1 h-4 w-px bg-[#E8E4DE]" aria-hidden />
                <ToolbarButton title="Bullet list" onClick={() => run("insertUnorderedList")}>• List</ToolbarButton>
                <ToolbarButton title="Numbered list" onClick={() => run("insertOrderedList")}>1. List</ToolbarButton>
                <ToolbarButton title="Blockquote" onClick={() => setBlock("blockquote")}>&ldquo; Quote</ToolbarButton>
                <ToolbarButton title="Link" onClick={addLink}>Link</ToolbarButton>
                <ToolbarButton title="Clear formatting" onClick={() => run("removeFormat")}>Clear</ToolbarButton>
            </div>
            <div
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-multiline="true"
                aria-label={placeholder}
                data-placeholder={placeholder}
                className="blog-editor-body px-[18px] py-4 text-[15px] leading-relaxed text-[#3D2817] outline-none"
                style={{ minHeight }}
                onInput={emitChange}
                onBlur={emitChange}
                suppressContentEditableWarning
            />
            <style>{`
                .blog-editor-body:empty::before {
                    content: attr(data-placeholder);
                    color: #9A9088;
                    pointer-events: none;
                }
                .blog-editor-body h2 {
                    font-family: Georgia, 'Times New Roman', serif;
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin: 1rem 0 0.5rem;
                    color: #4A0E17;
                }
                .blog-editor-body h3 {
                    font-family: Georgia, 'Times New Roman', serif;
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 0.85rem 0 0.4rem;
                    color: #4A0E17;
                }
                .blog-editor-body blockquote {
                    border-left: 3px solid #D4AF37;
                    margin: 0.75rem 0;
                    padding-left: 1rem;
                    color: #5C4033;
                }
                .blog-editor-body ul,
                .blog-editor-body ol {
                    margin: 0.5rem 0;
                    padding-left: 1.25rem;
                }
                .blog-editor-body a {
                    color: #C84B31;
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
}
